// tabs/EventsTab.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Event as EventIcon,
  MoreVert as MoreVertIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { API_BASE_URL } from 'config/api';
import {
  ActionButton,
  EmptyState,
  honeyColors,
} from '../styled';

const EventsTab = ({ events, community }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const getImageUrl = (image) => {
    if (!image) return null;
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  const handleMenuOpen = (event, eventItem) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleViewEvent = (eventItem) => {
    navigate(`/event/${eventItem.slug}`);
  };

  const handleEditEvent = (eventItem) => {
    navigate(`/event/${eventItem.slug}/edit`);
    handleMenuClose();
  };

  const handleCreateEvent = () => {
    navigate(`/community/${community.slug}/events/new`);
  };

  // Filter events based on tab
  const now = dayjs();
  const upcomingEvents = events.filter(e => dayjs(e.start).isAfter(now));
  const pastEvents = events.filter(e => dayjs(e.start).isBefore(now));
  const displayedEvents = tabValue === 0 ? upcomingEvents : pastEvents;

  const getEventStatus = (event) => {
    const start = dayjs(event.start);
    const end = dayjs(event.end);
    
    if (now.isBefore(start)) {
      const daysUntil = start.diff(now, 'day');
      if (daysUntil === 0) return { label: 'Today', color: honeyColors.success };
      if (daysUntil === 1) return { label: 'Tomorrow', color: honeyColors.primary };
      if (daysUntil <= 7) return { label: `In ${daysUntil} days`, color: honeyColors.info };
      return { label: 'Upcoming', color: honeyColors.textMuted };
    }
    
    if (now.isAfter(start) && now.isBefore(end)) {
      return { label: 'Happening Now', color: honeyColors.success };
    }
    
    return { label: 'Past', color: honeyColors.textMuted };
  };

  if (events.length === 0) {
    return (
      <EmptyState>
        <EventIcon sx={{ fontSize: 80, color: honeyColors.border, mb: 2 }} />
        <Typography variant="h5" color="textSecondary" gutterBottom>
          No Events Yet
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3, maxWidth: 400 }}>
          Create events to engage your community members and bring them together.
        </Typography>
        <ActionButton variant="contained" startIcon={<AddIcon />} onClick={handleCreateEvent}>
          Create Event
        </ActionButton>
      </EmptyState>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: honeyColors.textPrimary }}>
            Community Events
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {upcomingEvents.length} upcoming • {pastEvents.length} past events
          </Typography>
        </Box>
        <ActionButton variant="contained" startIcon={<AddIcon />} onClick={handleCreateEvent}>
          Create Event
        </ActionButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
          },
          '& .MuiTabs-indicator': {
            backgroundColor: honeyColors.primary,
          },
        }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarIcon fontSize="small" />
              Upcoming ({upcomingEvents.length})
            </Box>
          }
        />
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon fontSize="small" />
              Past ({pastEvents.length})
            </Box>
          }
        />
      </Tabs>

      {/* Events Grid */}
      {displayedEvents.length > 0 ? (
        <Grid container spacing={3}>
          {displayedEvents.map((event) => {
            const status = getEventStatus(event);
            
            return (
              <Grid item xs={12} sm={6} md={4} key={event.slug}>
                <Card
                  sx={{
                    borderRadius: '16px',
                    border: `1px solid ${honeyColors.border}`,
                    boxShadow: 'none',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: honeyColors.primary,
                      boxShadow: honeyColors.shadowLight,
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleViewEvent(event)}
                >
                  <CardMedia
                    component="div"
                    sx={{
                      height: 140,
                      backgroundColor: honeyColors.surfaceHoney,
                      backgroundImage: event.image ? `url(${getImageUrl(event.image)})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      p: 1.5,
                    }}
                  >
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        backgroundColor: status.color,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, event)}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: '#fff' },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </CardMedia>
                  
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '1rem' }}>
                      {event.name}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <TimeIcon sx={{ fontSize: 16, color: honeyColors.primary }} />
                      <Typography variant="body2" color="textSecondary">
                        {dayjs(event.start).format('ddd, MMM D • h:mm A')}
                      </Typography>
                    </Box>
                    
                    {event.venue?.name && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationIcon sx={{ fontSize: 16, color: honeyColors.textMuted }} />
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {event.venue.name}
                        </Typography>
                      </Box>
                    )}
                    
                    {event.attendees_count !== undefined && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <PeopleIcon sx={{ fontSize: 16, color: honeyColors.textMuted }} />
                        <Typography variant="body2" color="textSecondary">
                          {event.attendees_count} attendees
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent(event);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewEvent(event);
                      }}
                    >
                      View
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box textAlign="center" py={6}>
          <Typography color="textSecondary">
            {tabValue === 0 ? 'No upcoming events' : 'No past events'}
          </Typography>
        </Box>
      )}

      {/* Event Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleViewEvent(selectedEvent); handleMenuClose(); }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Event</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditEvent(selectedEvent)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit Event</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Manage Attendees</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: honeyColors.error }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: honeyColors.error }} /></ListItemIcon>
          <ListItemText>Cancel Event</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EventsTab;
